/**
 * Lump Sum Investment Plan advice-card stats from strategy status.
 *
 * Belongs here: find / under-review / ready copy for the lumpsum card.
 * Does not belong here: CTA posting or membership lock icons.
 */
import type {
  ElitePrimeAllocationPlanData,
  ElitePrimeStrategyData
} from '../../../../types/elitePrimeSummary.types'
import type { AdviceCardStat, ElitePlansAdviceCardInsightTone } from '../../types/adviceCards.types'
import {
  buildStrategyFindCopyDisplay,
  buildStrategyUnderReviewStats,
  isStrategyFindCopyStatus,
  isStrategyQuestionnaireSubmittedStatus
} from './adviceCardStrategyStatusDisplay'

export type LumpSumInvestmentPlanAdviceCardDisplay = {
  stats: AdviceCardStat[]
  insightTone?: ElitePlansAdviceCardInsightTone
}

/**
 * Maps strategy status to Lump Sum Investment Plan insight strip copy.
 *
 * - Find: questionnaire not started / in progress
 * - Under review: questionnaire submitted — days from `allocationPlanData.planAvailableInDays`
 * - Ready: plan rolled out
 */
export function buildLumpSumInvestmentPlanAdviceCardDisplay({
  strategyData,
  allocationPlanData
}: {
  strategyData?: ElitePrimeStrategyData
  allocationPlanData?: ElitePrimeAllocationPlanData
}): LumpSumInvestmentPlanAdviceCardDisplay {
  const status = strategyData?.status

  if (isStrategyFindCopyStatus(status)) {
    return buildStrategyFindCopyDisplay(
      'Find your allocation strategy to get your Lump Sum plan'
    )
  }

  if (isStrategyQuestionnaireSubmittedStatus(status)) {
    return {
      stats: buildStrategyUnderReviewStats({
        planLabel: 'Lump Sum plan',
        planAvailableAt: allocationPlanData?.planAvailableAt,
        planAvailableInDays: allocationPlanData?.planAvailableInDays
      }),
      insightTone: 'orange'
    }
  }

  // PLAN_ROLLED_OUT
  return {
    stats: [{ label: 'Lump Sum plan is ready', emphasize: true }],
    insightTone: 'orange'
  }
}
